import { Api } from '@axiom/ui';
import type {
  CandidateReputationDetailsRequestBody,
  CandidateReputationDetailsResponseBody,
  TimeZoneIanaAll,
  User,
  UserInterview,
} from '@axiom/validation';
import type { InterviewOwner, InterviewsStatus } from '@axiom/const';

import { EnvUtil } from '../utils/env-util';

export type ReadUserInterviewsType = {
  data: UserInterview[];
  meta: {
    aggregations: Record<string, unknown> | null;
    currentPage: number;
    pageCount: number;
    resultCount: number;
    searchAfter: [string] | null;
  };
};

export type ReadUserInterviewsBody = {
  filters: {
    assigneeType: keyof typeof InterviewOwner | null;
    interviewStatus:
      (typeof InterviewsStatus)[keyof typeof InterviewsStatus] | null;
  };
  sort: string;
  order: 'asc' | 'desc' | null;
  page: number;
  pageSize: number;
  timezone: TimeZoneIanaAll;
};

// Holds the two user reads not yet moved onto generated clients: interviews
// (→ UserApi.searchUserSpecificOpportunityInterviews) and talent
// reputation details (→ ReputationDetailsApi.reputationDetailsSearch). Kept
// hand-written + schema-less because the generated DTO shapes differ from the
// @axiom/validation types their consumers use. Named distinctly so it doesn't
// collide with the generated UserApi (the apiUnitHelper scanner keys on class
// name, and a duplicate would shadow the generated twin).
class UserActivityApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiEnvoyUrl,
    });
  }

  readUserInterviews({
    userId,
    body,
  }: {
    userId: User['id'];
    body: ReadUserInterviewsBody;
  }) {
    return super.read<ReadUserInterviewsType>({
      endpoint: `/users/${userId}/opportunities/interviews`,
      method: 'POST',
      body,
    });
  }

  readUserTalentReputationDetails({
    userId,
    body,
  }: {
    userId: User['id'];
    body: CandidateReputationDetailsRequestBody;
  }) {
    return super.read<CandidateReputationDetailsResponseBody>({
      endpoint: `/users/${userId}/reputationDetails`,
      method: 'POST',
      body,
    });
  }

  async refreshUserTalentReputationDetails({ userId }: { userId: User['id'] }) {
    return super.invalidate(`/users/${userId}/reputationDetails`);
  }
}

export const UserActivityApi = new UserActivityApiClass();
